import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useTenant } from "../hooks/useTenant";
import { usePlatform } from "../contexts/PlatformContext";
import PageLoader from "../components/Common/PageLoader";

/**
 * PlatformProtectedRoute — Guard para rotas do painel que exigem permissão específica.
 *
 * Verifica em sequência:
 * 1. Usuário autenticado
 * 2. role === "super_admin" (via claims)
 * 3. platformPermission exigida pela rota (via PlatformContext)
 *
 * Uso em allRoutes.js:
 *   <PlatformProtectedRoute platformPermission="platform_billing">
 *     <PlatformBilling />
 *   </PlatformProtectedRoute>
 */
const PlatformProtectedRoute = ({ children, platformPermission }) => {
    const { isAuthenticated, loading: authLoading } = useAuth();
    const { claims, loading: tenantLoading } = useTenant();
    const { canPlatform, loading: platformLoading } = usePlatform();

    if (authLoading || (isAuthenticated && (tenantLoading || platformLoading))) {
        return <PageLoader />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (claims?.role !== "super_admin") {
        return <Navigate to="/home" replace />;
    }

    if (platformPermission && !canPlatform(platformPermission)) {
        return <Navigate to="/platform" replace />;
    }

    return <>{children}</>;
};

export default PlatformProtectedRoute;
